import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore, useCartStore, useWishlistStore, useCompareStore } from '../store';
import { toast } from 'react-toastify';
import './Header.css';

export default function Header() {
  const [scrolled, setScrolled]     = useState(false);
  const [menuOpen, setMenuOpen]      = useState(false);
  const [searchOpen, setSearchOpen]  = useState(false);
  const [searchQuery, setSearchQuery]= useState('');
  const { isAuthenticated, user, logout } = useAuthStore();
  const cartCount    = useCartStore(s => s.items.reduce((n, i) => n + i.quantity, 0));
  const wishCount    = useWishlistStore(s => s.items.length);
  const compareCount = useCompareStore(s => s.items.length);
  const navigate     = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  return (
    <>
      {/* Top bar */}
      <div className="header-topbar">
        <div className="container">
          <span>🔬 For Research Purposes Only · Not for Human Consumption</span>
          <div className="topbar-links">
            <Link to="/certificates">3rd Party Testing</Link>
            <Link to="/faq">FAQ</Link>
            <Link to="/contact">Support</Link>
          </div>
        </div>
      </div>

      {/* Main header */}
      <header className={`header${scrolled ? ' header--scrolled' : ''}`}>
        <div className="container header__inner">
          {/* Logo */}
          <Link to="/" className="header__logo">
            <span className="logo-icon">🧬</span>
            <div>
              <span className="logo-text">PeptideLab<strong>Pro</strong></span>
              <span className="logo-sub">Premium Research Peptides</span>
            </div>
          </Link>

          {/* Nav */}
          <nav className={`header__nav${menuOpen ? ' header__nav--open' : ''}`}>
            <Link to="/"             onClick={() => setMenuOpen(false)}>Home</Link>
            <Link to="/shop"         onClick={() => setMenuOpen(false)}>Shop</Link>
            <Link to="/blog"         onClick={() => setMenuOpen(false)}>Blog</Link>
            <Link to="/certificates" onClick={() => setMenuOpen(false)}>Testing</Link>
            <Link to="/calculator"   onClick={() => setMenuOpen(false)}>Calculator</Link>
            <Link to="/about"        onClick={() => setMenuOpen(false)}>About</Link>
            <Link to="/contact"      onClick={() => setMenuOpen(false)}>Contact</Link>
          </nav>

          {/* Actions */}
          <div className="header__actions">
            <button className="icon-btn" onClick={() => setSearchOpen(v => !v)} title="Search">
              🔍
            </button>
            <Link to="/wishlist" className="icon-btn" title="Wishlist">
              ❤️ {wishCount > 0 && <span className="badge-count">{wishCount}</span>}
            </Link>
            <Link to="/compare" className="icon-btn" title="Compare">
              ⚖️ {compareCount > 0 && <span className="badge-count">{compareCount}</span>}
            </Link>
            <Link to="/cart" className="icon-btn" title="Cart">
              🛒 {cartCount > 0 && <span className="badge-count">{cartCount}</span>}
            </Link>
            {isAuthenticated ? (
              <div className="user-menu">
                <button className="btn btn-sm btn-primary">{user?.firstName || 'Account'} ▾</button>
                <div className="user-dropdown">
                  <Link to="/profile">My Profile</Link>
                  <Link to="/orders">My Orders</Link>
                  <button onClick={handleLogout}>Logout</button>
                </div>
              </div>
            ) : (
              <Link to="/login" className="btn btn-sm btn-primary">Login</Link>
            )}
            <button className="hamburger" onClick={() => setMenuOpen(v => !v)}>
              {menuOpen ? '✕' : '☰'}
            </button>
          </div>
        </div>

        {/* Search bar */}
        {searchOpen && (
          <div className="search-bar">
            <div className="container">
              <form onSubmit={handleSearch} className="search-form">
                <input
                  autoFocus
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search peptides, research compounds…"
                  className="form-input"
                />
                <button type="submit" className="btn btn-primary">Search</button>
                <button type="button" className="btn btn-secondary" onClick={() => setSearchOpen(false)}>✕</button>
              </form>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
